import { useEffect, useRef, useState } from 'react';

import {
  effectiveChildTokens,
  type HierarchicalFilterGroup,
} from './StaffingPage.filters';
import styles from './StaffingHierarchyFilter.module.css';

interface Props {
  label: string;
  groups: HierarchicalFilterGroup[];
  selected: Set<string>;
  onChange: (selected: Set<string>) => void;
}

export function StaffingHierarchyFilter({ label, groups, selected, onChange }: Props) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const effective = effectiveChildTokens(selected, groups);
  const selectedCount = effective.size;
  const displayText = selectedCount === 0
    ? `${label}: All`
    : `${label}: ${selectedCount} selected`;

  useEffect(() => {
    if (!open) return;
    function handleClick(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [open]);

  function clear() {
    onChange(new Set());
  }

  function toggleGroup(group: HierarchicalFilterGroup) {
    const next = new Set(selected);
    const childTokens = group.children.map((child) => child.token);
    const allSelected = childTokens.length > 0 && childTokens.every((token) => effective.has(token));

    next.delete(group.token);
    for (const token of childTokens) next.delete(token);
    if (!allSelected) next.add(group.token);
    onChange(next);
  }

  function toggleChild(group: HierarchicalFilterGroup, childToken: string) {
    const next = new Set(selected);
    const childTokens = group.children.map((child) => child.token);

    if (next.has(group.token)) {
      next.delete(group.token);
      for (const token of childTokens) {
        if (token !== childToken) next.add(token);
      }
    } else if (next.has(childToken)) {
      next.delete(childToken);
    } else {
      next.add(childToken);
    }

    const selectedChildren = childTokens.filter((token) => next.has(token));
    if (childTokens.length > 0 && selectedChildren.length === childTokens.length) {
      for (const token of childTokens) next.delete(token);
      next.add(group.token);
    }

    onChange(next);
  }

  return (
    <div className={styles.root} ref={ref}>
      <button
        className={`${styles.trigger} ${selectedCount > 0 ? styles.triggerActive : ''}`}
        onClick={() => setOpen((v) => !v)}
        type="button"
      >
        {displayText}
        <span className={styles.chevron}>{open ? '\u25B2' : '\u25BC'}</span>
      </button>
      {open && (
        <div className={styles.dropdown}>
          {selectedCount > 0 && (
            <button className={styles.clearBtn} onClick={clear} type="button">
              Clear
            </button>
          )}
          {groups.length === 0 && (
            <div className={styles.empty}>No options</div>
          )}
          {groups.map((group) => {
            const childTokens = group.children.map((child) => child.token);
            const checked = childTokens.length > 0 && childTokens.every((token) => effective.has(token));
            const indeterminate = !checked && childTokens.some((token) => effective.has(token));
            return (
              <div key={group.token} className={styles.group}>
                <label className={styles.groupRow}>
                  <IndeterminateCheckbox
                    checked={checked}
                    indeterminate={indeterminate}
                    onChange={() => toggleGroup(group)}
                  />
                  <span className={styles.groupLabel}>{group.label}</span>
                  <span className={styles.groupCount}>{group.children.length}</span>
                </label>
                {group.children.map((child) => (
                  <label key={child.token} className={styles.childRow}>
                    <input
                      className={styles.checkbox}
                      type="checkbox"
                      checked={effective.has(child.token)}
                      onChange={() => toggleChild(group, child.token)}
                    />
                    <span>{child.label}</span>
                  </label>
                ))}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

function IndeterminateCheckbox({
  checked,
  indeterminate,
  onChange,
}: {
  checked: boolean;
  indeterminate: boolean;
  onChange: () => void;
}) {
  const ref = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (ref.current) ref.current.indeterminate = indeterminate;
  }, [indeterminate]);

  return (
    <input
      ref={ref}
      className={styles.checkbox}
      type="checkbox"
      checked={checked}
      onChange={onChange}
      aria-checked={indeterminate ? 'mixed' : checked}
    />
  );
}
